import { DeleteOutlined } from '@ant-design/icons'
import { Button, Col, Flex, Form, Row, Select, Spin, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { FilterListBean, FilterListGlobalBean } from 'common/api/api'
import { getFilterListTypeName } from 'common/api/dictionaries'
import { filterListApi } from 'common/api/entitiesApi'
import { FilterListTypeSelector } from 'common/components/dictionary/FilterListTypeSelector'
import { useAppContext } from 'common/context/AppContext'
import { showError } from 'common/util/Util'
import { useEffect, useMemo, useState } from 'react'
import TypeEnum = FilterListBean.TypeEnum

interface Item {
  id: number
  name: string
  processed?: boolean
  type: TypeEnum
}

interface Props {
  entity: FilterListGlobalBean
  setEntity: (entity: FilterListGlobalBean) => void
  admin?: boolean
  agency?: boolean
}

const FilterListGlobalCard = ({ entity, setEntity, admin, agency }: Props) => {
  const appContext = useAppContext()
  const [loading, setLoading] = useState(false)

  const [filterListType, setFilterListType] = useState<TypeEnum | undefined>(undefined)
  const [filterListAll, setFilterListAll] = useState<Array<FilterListBean>>([])
  const [filterListOptions, setFilterListOptions] = useState<any | undefined>([])
  const [filterListSelected, setFilterListSelected] = useState<Item[]>([])
  const [filterListSelectKey, setFilterListSelectKey] = useState(0)

  const isAllowedByOwner = (fl: FilterListBean) => {
    const advertiserId = fl.advertiserId
    const agencyId = fl.agencyId

    // admin => only SuperAdmin lists (no advertiserId and no agencyId)
    if (admin) {
      return advertiserId == null && agencyId == null
    }

    // agency => only agency lists (agencyId != null AND advertiserId == null)
    if (agency) {
      return agencyId != null && advertiserId == null
    }

    // advertiser UI => only advertiser lists (advertiserId != null)
    return advertiserId != null
  }

  useEffect(() => {
    setLoading(true)
    filterListApi
      .getPageFilterLists('name', 'asc')
      .then((filterListItems) => {
        const filterLists = (filterListItems.data || []).flatMap((item) => (item.data ? [item.data] : []))
        setFilterListAll(filterLists.filter(isAllowedByOwner))
      })
      .catch((e) => showError(appContext, e))
      .finally(() => setLoading(false))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    updateSelected(entity.filterListId, filterListAll)
    updateOptions(filterListType, entity.filterListId, filterListAll)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entity.filterListId, filterListAll])

  const changeFilterListType = (type: TypeEnum) => {
    setFilterListType(type)
    updateOptions(type, entity.filterListId, filterListAll)
  }

  const link = (filterListId: number) => {
    setEntity({ ...entity, filterListId })
    setFilterListSelectKey((k) => k + 1)
    updateSelected(filterListId, filterListAll)
    updateOptions(filterListType, filterListId, filterListAll)
  }

  const unlink = () => {
    setEntity({ ...entity, filterListId: undefined as any })
    updateSelected(undefined, filterListAll)
    updateOptions(filterListType, undefined, filterListAll)
  }

  function updateSelected(filterListId: number | undefined, all: Array<FilterListBean>) {
    if (!filterListId) {
      setFilterListSelected([])
      return
    }
    const fl = all.find((x) => x.id === filterListId)
    if (!fl) {
      setFilterListSelected([])
      return
    }
    setFilterListSelected([
      {
        id: fl.id!,
        name: fl.name!,
        type: fl.type!,
        processed: fl.processed,
      } as Item,
    ])
  }

  function updateOptions(type: TypeEnum | undefined, selectedId: number | undefined, all: Array<FilterListBean>) {
    setFilterListOptions(
      all
        .filter((fl) => fl.type === type)
        .filter((fl) => !selectedId || fl.id !== selectedId)
        .map((f) => ({ value: f.id, label: f.name })),
    )
  }

  const filterListColumns: ColumnsType<any> = useMemo(
    () => [
      {
        title: 'Type',
        key: 'type',
        dataIndex: 'type',
        width: 200,
        render: (value) => getFilterListTypeName(value),
      },
      {
        title: 'Selected List',
        key: 'name',
        dataIndex: 'name',
      },
      {
        title: 'Status',
        dataIndex: 'processed',
        sorter: true,
        width: 100,
        render: (_: any, record: Item) => (
          <Tag color="#D6E6FF" style={{ color: '#0D1A43', textAlign: 'center' }}>
            {record.processed ? 'Processed' : 'Pending'}
          </Tag>
        ),
      },
      {
        title: 'Action',
        key: 'action',
        dataIndex: 'action',
        width: 80,
        render: (_: any, record: Item) =>
          record.id ? (
            <Button onClick={unlink} type="text" danger>
              <DeleteOutlined />
            </Button>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [entity.filterListId, filterListAll],
  )

  return (
    <Spin spinning={loading}>
      <Form.Item label="Filter List" tooltip="Select the specific filter list to apply globally.">
        <Flex gap={12}>
          <FilterListTypeSelector
            placeholder="Select Type"
            value={filterListType}
            onChange={(type) => changeFilterListType(type)}
            style={{ width: 220 }}
          />
          <Select
            style={{ flex: 1 }}
            placeholder="Select Filter List"
            key={filterListSelectKey}
            options={filterListOptions}
            onChange={(value) => link(value)}
            disabled={!filterListType || !!entity.filterListId}
            showSearch
          />
        </Flex>
      </Form.Item>

      {entity.filterListId && filterListSelected.length > 0 && (
        <Row>
          <Col span="4"></Col>
          <Col span="16">
            <Table
              className="creatives-table"
              columns={filterListColumns}
              pagination={false}
              dataSource={filterListSelected}
            />
          </Col>
        </Row>
      )}
    </Spin>
  )
}

export default FilterListGlobalCard
